import { AB_CAMPAIGNS } from "@/internals/constants/testAB/testAB";
import { getDomainConfig } from "@/sites/config/domainConfigs";
import { ABTestCampaignManager } from "./providers/ABTestCampaignManager";
import { ABTestProviderFactory } from "./providers/ABTestProviderFactory";
import { ABCampaigns, IABTestProvider } from "./types";
import { isClient } from "@/utils/browser";

/**
 * Main AB Test Service - Orchestrates campaigns and providers
 * Follows Dependency Inversion Principle and Single Responsibility Principle
 */
export class ABTestingService {
  private campaignManager: ABTestCampaignManager;
  private provider: IABTestProvider | null = null;
  private isInitialized = false;

  constructor(campaigns: ABCampaigns = AB_CAMPAIGNS) {
    this.campaignManager = new ABTestCampaignManager(campaigns);
  }

  /**
   * Initialize the AB test service with the appropriate provider based on domain config
   */
  async initialize(hostname?: string): Promise<void> {
    if (this.isInitialized) {
      console.warn("ABTestService is already initialized");
      return;
    }

    try {
      // Get domain-specific AB test configuration
      const currentHostname = hostname || (isClient() ? window.location.hostname : "");
      const domainConfig = getDomainConfig(currentHostname);
      if (!domainConfig.abTestConfig?.enabled) {
        return;
      }

      // Create and initialize provider
      this.provider = ABTestProviderFactory.createProvider(domainConfig.abTestConfig);
      await this.provider.initialize();
      await this.loadVariationsFromProvider();

      this.isInitialized = true;
    } catch (error) {
      console.error("Failed to initialize AB Test Service:", error);
    }
  }

  private async loadVariationsFromProvider(): Promise<void> {
    if (!this.provider) {
      return;
    }

    const maxAttempts = 20;
    let attempts = 0;

    while (!this.provider.isReady() && attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }

    if (!this.provider.isReady()) {
      console.warn("AB Test Provider is not ready after timeout");
      return;
    }

    const variations = this.provider.getAssignedVariation();
    if (variations) {
      this.campaignManager.updateCampaignsFromVariations(variations);
    }
  }

  getVariant(campaignKey: string): string | undefined {
    return this.campaignManager.getVariant(campaignKey);
  }

  isVariantActive(campaignKey: string, variantName: string): boolean {
    const activeVariant = this.getVariant(campaignKey);
    return activeVariant === variantName;
  }

  getAllCampaigns(): ABCampaigns {
    return this.campaignManager.getAllCampaigns();
  }

  trackEvent(eventName: string, properties?: Record<string, any>): void {
    if (this.provider?.trackEvent) {
      this.provider.trackEvent(eventName, properties);
    }
  }

  isReady(): boolean {
    return this.isInitialized && (this.provider?.isReady() ?? false);
  }

  getProviderType(): string | null {
    return this.provider?.getProviderConfig()?.provider || null;
  }
}

// Global singleton instance
let abTestServiceInstance: ABTestingService | null = null;

export const getABTestingService = (): ABTestingService => {
  if (!abTestServiceInstance) {
    abTestServiceInstance = new ABTestingService();
  }
  return abTestServiceInstance;
};
